import React, { useState, useMemo, useRef, useEffect } from 'react';
import { fetchInstagramInbox, sendInstagramInboxMessage, markInstagramThreadRead } from '../../utils/socialApi';
import { subscribeInboxMessages } from '../../utils/inboxSocket';

// ============================================================================
// SocialInboxView — WhatsApp-desktop style inbox. Conversations are loaded
// from /api/social/instagram/inbox, which aggregates rows from
// instagram_dm_logs (auto-DMs the dispatcher sent) + instagram_comments
// (the comment that triggered each DM).
// ============================================================================

const LABELS = [
    { id: 'all', name: 'All' },
    { id: 'dm', name: 'DMs' },
    { id: 'comment', name: 'Comments' },
];

// Detect the browser's IANA timezone once and reuse for every formatter.
// `toLocaleString` uses this by default, but we pass it explicitly so the
// behavior is observable in DevTools (timeZone field on the call) and so
// any future test harness that mocks Intl can override it cleanly.
const BROWSER_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
})();

function formatRel(ts) {
    // Diff in seconds — `Date.now()` and `ts` are both absolute UTC ms so
    // this is timezone-independent. Up to 7 days we show relative
    // (now/Xm/Xh/Xd); older switches to a browser-local date label so the
    // sidebar stays readable for old conversations.
    const diff = (Date.now() - ts) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
    const d = new Date(ts);
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleDateString('en-US', sameYear
        ? { month: 'short', day: 'numeric', timeZone: BROWSER_TZ }
        : { year: '2-digit', month: 'short', day: 'numeric', timeZone: BROWSER_TZ });
}
function formatTime(ts) {
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: BROWSER_TZ });
}
/**
 * Wall-clock time + date in the browser's local timezone. Used on message
 * bubbles so each message reads correctly even when the thread spans days —
 * the day separators give large-grain context, this gives the per-message
 * timestamp. `timeZone: BROWSER_TZ` is explicit so the rendered string is
 * always in the user's perceived clock, regardless of any host environment
 * default.
 */
function formatBubbleTimestamp(ts) {
    const d = new Date(ts);
    const todayKey = dayKey(Date.now());
    const k = dayKey(ts);
    if (k === todayKey) {
        return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: BROWSER_TZ });
    }
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleString('en-US', sameYear
        ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: BROWSER_TZ }
        : { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: BROWSER_TZ });
}
/**
 * Stable per-local-day key (e.g. "2026-04-28") for grouping messages into
 * day buckets. Built from `toLocaleDateString` parts so the bucketing
 * matches the displayed bubble date — falls back to year/month/date when
 * `formatToParts` isn't available.
 */
function dayKey(ts) {
    const d = new Date(ts);
    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit', timeZone: BROWSER_TZ,
        }).format(d);
        return parts; // en-CA gives "YYYY-MM-DD"
    } catch {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
}
function formatDayLabel(ts) {
    const today = new Date();
    const todayK = dayKey(today.getTime());
    const yK = dayKey(today.getTime() - 86400000);
    const k = dayKey(ts);
    if (k === todayK) return 'Today';
    if (k === yK) return 'Yesterday';
    const d = new Date(ts);
    const sameYear = d.getFullYear() === today.getFullYear();
    return d.toLocaleDateString('en-US', sameYear
        ? { weekday: 'short', month: 'short', day: 'numeric', timeZone: BROWSER_TZ }
        : { year: 'numeric', month: 'short', day: 'numeric', timeZone: BROWSER_TZ });
}
function formatAgo(ts) {
    const d = (Date.now() - ts) / 1000;
    if (d < 60) return 'just now';
    if (d < 3600) return `${Math.floor(d / 60)} min ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    const days = Math.floor(d / 86400);
    if (days < 7) return `${days}d ago`;
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: BROWSER_TZ });
}
function formatCount(n) {
    if (n < 1000) return String(n);
    if (n < 1000000) return (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(/\.0$/, '') + 'K';
    return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
}

function ConnectModal({ onConnect, onClose, locked = false, busy = false }) {
    return (
        <>
            <div onClick={locked ? undefined : onClose} style={{
                position: 'absolute', inset: 0, zIndex: 40,
                background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(3px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }} />
            <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 50,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
            }}><div style={{
                pointerEvents: 'auto',
                width: 440, background: '#fff', borderRadius: 16,
                boxShadow: '0 24px 60px -12px rgba(0,0,0,0.2), 0 0 0 1px rgba(0,0,0,0.05)',
                overflow: 'hidden',
            }}>
                {/* Preview area */}
                <div style={{
                    height: 200, position: 'relative', overflow: 'hidden',
                    background: 'linear-gradient(135deg, #fce7f3 0%, #fef3c7 50%, #e0e7ff 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        width: 280, height: 160, background: '#fff', borderRadius: 12,
                        boxShadow: '0 20px 40px -12px rgba(0,0,0,0.15)',
                        padding: 16, display: 'flex', flexDirection: 'column',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                </svg>
                            </div>
                            <span style={{ fontSize: 11, fontWeight: 600, color: '#1f2937' }}>Social Scheduler</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, flex: 1 }}>
                            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                                <div key={i} style={{ fontSize: 7, color: '#a8a29e', textAlign: 'center', fontWeight: 600 }}>{d}</div>
                            ))}
                            {Array.from({ length: 14 }).map((_, i) => (
                                <div key={i} style={{
                                    borderRadius: 4, background: [2, 5, 8, 11].includes(i) ? 'linear-gradient(135deg, #fecdd3, #fde68a)' : '#f5f5f4',
                                    aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 6, color: '#78716c',
                                }}>
                                    {[2, 5, 8, 11].includes(i) ? ['☕', '🌅', '🌸', '📚'][Math.floor(Math.random() * 4)] : ''}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: '28px 32px 32px', textAlign: 'center' }}>
                    <h2 style={{ fontSize: 22, fontWeight: 600, color: '#1f2937', margin: '0 0 8px', letterSpacing: '-0.02em' }}>
                        Connect Instagram
                    </h2>
                    <p style={{ fontSize: 14, color: '#78716c', margin: '0 0 24px', lineHeight: 1.6 }}>
                        Connect your Instagram account to view and reply to messages from your audience.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <style>{`@keyframes igSpin { to { transform: rotate(360deg); } }`}</style>
                        <button onClick={onConnect} disabled={busy} style={{
                            width: '100%', padding: '12px 20px', borderRadius: 10, border: 'none',
                            fontSize: 14, fontWeight: 600, color: '#fff', cursor: busy ? 'not-allowed' : 'pointer',
                            background: '#111827', boxShadow: '0 4px 12px -4px rgba(0,0,0,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            transition: 'transform 0.15s', opacity: busy ? 0.7 : 1,
                        }}
                            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.transform = 'scale(1.02)'; }}
                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        >
                            {busy ? (
                                <>
                                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'igSpin 0.6s linear infinite' }} />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                                    </svg>
                                    Continue with Instagram
                                </>
                            )}
                        </button>
                        {!locked && (
                            <button onClick={onClose} style={{
                                width: '100%', padding: '10px 20px', borderRadius: 10,
                                border: '1px solid #e7e5e4', background: '#fff',
                                fontSize: 13, fontWeight: 500, color: '#78716c', cursor: 'pointer',
                                transition: 'all 0.15s',
                            }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#f5f5f4'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
                            >
                                Maybe later
                            </button>
                        )}
                    </div>
                    <p style={{ fontSize: 11, color: '#a8a29e', margin: '16px 0 0', lineHeight: 1.4 }}>
                        We'll only request the permissions we need to publish on your behalf.
                    </p>
                </div>
            </div></div>
        </>
    );
}

export default function SocialInboxView({ instagramConnected = false, onInstagramConnect, instagramBusy = false, accessToken }) {
    const showConnect = !instagramConnected;
    const [conversations, setConversations] = useState([]);
    const [activeId, setActiveId] = useState(null);
    const [activeLabel, setActiveLabel] = useState('all');
    const [search, setSearch] = useState('');
    const [text, setText] = useState('');
    const scrollRef = useRef(null);
    // Post-details drawer (only meaningful for comment threads)
    const [infoPanelOpen, setInfoPanelOpen] = useState(false);
    const [panelMounted, setPanelMounted] = useState(false);
    const [panelExiting, setPanelExiting] = useState(false);

    // Fetch live DM conversations whenever the token is available, then poll
    // every 10 seconds to pick up inbound messages delivered via webhook.
    //
    // Reconciliation is dedupe-by-id, NOT a wholesale state replacement.
    // The previous implementation replaced the conversations array on every
    // poll which lost any local message whose row wasn't yet visible in
    // the next fetch (slow DB commit, replication lag, request raced with
    // an in-flight send, etc.) — the symptom was messages flickering away.
    // Now: server messages are authoritative for any id present in the
    // fetch response; local-only messages (optimistic temps + recently
    // persisted rows the next poll hasn't seen yet) are preserved until
    // the server returns them, after which they merge by id.
    useEffect(() => {
        if (!accessToken || !instagramConnected) return;
        let cancelled = false;

        // Every message has `at` as absolute UTC milliseconds (server uses
        // .getTime(); optimistic temps use Date.now() — both timezone-
        // independent). Sorting ascending always gives chronological order
        // regardless of where the merchant is. The display layer formats
        // each timestamp through toLocaleTimeString() which renders in the
        // browser's local timezone, so the wall-clock time on each bubble
        // is correct for the merchant viewing it.
        const sortByAt = (arr) => [...arr].sort((a, b) => (a.at ?? 0) - (b.at ?? 0));

        const reconcile = (prev, incoming) => {
            const prevById = new Map(prev.map((c) => [c.id, c]));
            const seen = new Set();
            const merged = incoming.map((srv) => {
                seen.add(srv.id);
                const local = prevById.get(srv.id);
                // No prior local state → trust server, but defensively sort
                // in case the server response slipped out of order.
                if (!local) return { ...srv, messages: sortByAt(srv.messages) };
                // Merge messages: server wins on id collisions; any
                // local-only ids (pending/failed temps, just-sent rows)
                // are appended then re-sorted so chronology holds.
                const srvIds = new Set(srv.messages.map((m) => m.id));
                const localOnly = (local.messages ?? []).filter((m) => !srvIds.has(m.id));
                const messages = sortByAt([...srv.messages, ...localOnly]);
                return {
                    ...srv,
                    unread: local.unread ?? srv.unread,
                    messages,
                    lastAt: messages[messages.length - 1]?.at ?? srv.lastAt,
                };
            });
            // Keep purely local conversations (e.g. brand-new threads with
            // optimistic temps that haven't shown up server-side yet) so
            // they don't blink out of existence during their first poll.
            for (const c of prev) {
                if (!seen.has(c.id)) merged.push({ ...c, messages: sortByAt(c.messages ?? []) });
            }
            return merged;
        };

        const load = async () => {
            const res = await fetchInstagramInbox({ accessToken });
            if (cancelled || !res.ok || !Array.isArray(res.data?.conversations)) return;
            setConversations((prev) => reconcile(prev, res.data.conversations));
            setActiveId((prev) => prev ?? res.data.conversations[0]?.id ?? null);
        };

        load();
        const t = setInterval(load, 10000);
        return () => { cancelled = true; clearInterval(t); };
    }, [accessToken, instagramConnected]);

    // Real-time push: server emits `inbox:message` from the webhook handler
    // (inbound DMs), the auto-DM dispatcher (comment + auto reply), and the
    // manual-send endpoint. We dedupe by message id against current state and
    // only count toward unread when the event isn't for the active thread.
    useEffect(() => {
        if (!accessToken || !instagramConnected) return;
        const sortByAt = (arr) => [...arr].sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
        const off = subscribeInboxMessages(accessToken, (event) => {
            if (!event?.message || !event?.threadId) return;
            setConversations((prev) => {
                const idx = prev.findIndex((c) => c.id === event.threadId);
                if (idx === -1) {
                    // First time we see this thread — synthesize a minimal
                    // conversation; the next poll will fill in handle/avatar
                    // properly from the inbox endpoint.
                    return [
                        {
                            id: event.threadId,
                            type: 'dm',
                            handle: event.handle || `user_${(event.threadUserId || '').slice(-6)}`,
                            avatarEmoji: '🌸',
                            threadUserId: event.threadUserId,
                            unread: event.direction === 'inbound' ? 1 : 0,
                            lastAt: event.message.at,
                            messages: [event.message],
                        },
                        ...prev,
                    ];
                }
                const c = prev[idx];
                // Dedup — server may emit the same message a second time on
                // retry, and our send path also triggers an emit that the
                // sender already optimistically rendered.
                if (c.messages.some((m) => m.id === event.message.id)) return prev;
                const messages = sortByAt([...c.messages, event.message]);
                const isActive = c.id === activeId;
                const updated = {
                    ...c,
                    messages,
                    lastAt: messages[messages.length - 1]?.at ?? c.lastAt,
                    unread: event.direction === 'inbound' && !isActive ? (c.unread ?? 0) + 1 : c.unread,
                    handle: event.handle || c.handle,
                };
                const next = prev.slice();
                next[idx] = updated;
                return next;
            });
        });
        return off;
    }, [accessToken, instagramConnected, activeId]);

    const filtered = useMemo(() => {
        let list = [...conversations].sort((a, b) => b.lastAt - a.lastAt);
        // DMs and Comments are surfaced as mutually exclusive views: a
        // comment-triggered thread carries `type: 'comment'` for its full
        // lifetime (the postPreview drawer is the differentiator), so the
        // "DMs" tab is restricted to pure DM threads only. "All" still
        // shows the union.
        if (activeLabel === 'dm') list = list.filter((c) => c.type === 'dm');
        if (activeLabel === 'comment') list = list.filter((c) => c.type === 'comment');
        const q = search.trim().toLowerCase();
        if (q) list = list.filter((c) => c.handle.toLowerCase().includes(q));
        return list;
    }, [conversations, activeLabel, search]);

    const active = conversations.find((c) => c.id === activeId) || filtered[0];
    const totalUnread = conversations.reduce((s, c) => s + c.unread, 0);

    const openConvo = (id) => {
        setActiveId(id);
        // Optimistic clear of the unread badge — server is told via
        // /mark-read which marks every inbound row in this thread read_at=NOW().
        setConversations((p) => p.map((c) => c.id === id && c.unread > 0 ? { ...c, unread: 0 } : c));
        const opened = conversations.find((c) => c.id === id);
        // Comment items reuse the commenter's IGSID as `threadUserId` for
        // future "reply via DM" support, but they have no DM rows of their
        // own — calling /mark-read here would mistakenly clear unread on
        // the user's actual DM thread.
        if (opened?.threadUserId && opened.type !== 'comment' && accessToken) {
            markInstagramThreadRead({ threadUserId: opened.threadUserId, accessToken }).catch(() => {});
        }
    };

    const sendMessage = async () => {
        const t = text.trim();
        if (!t || !active || !active.threadUserId || !accessToken) return;
        // Optimistic — push the message into the thread immediately so the
        // composer feels instant. Mark it pending until the API confirms.
        // `Date.now()` and the server's `.getTime()` both return absolute
        // UTC milliseconds so the temp slots into the chronologically
        // correct position regardless of merchant timezone.
        const tempId = `tmp-${Date.now()}`;
        const now = Date.now();
        const sortByAt = (arr) => [...arr].sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
        setConversations((p) => p.map((c) => {
            if (c.id !== active.id) return c;
            const messages = sortByAt([...c.messages, { id: tempId, from: 'me', text: t, at: now, pending: true }]);
            return { ...c, messages, lastAt: messages[messages.length - 1]?.at ?? now };
        }));
        setText('');

        const res = await sendInstagramInboxMessage({
            threadUserId: active.threadUserId,
            text: t,
            accessToken,
        });
        setConversations((p) => p.map((c) => {
            if (c.id !== active.id) return c;
            const swapped = c.messages.map((m) => {
                if (m.id !== tempId) return m;
                if (res.ok) {
                    // Replace temp row with the persisted one from the server.
                    return { ...res.data.message };
                }
                return { ...m, pending: false, failed: true, errorMessage: res.error };
            });
            const messages = sortByAt(swapped);
            return { ...c, messages, lastAt: messages[messages.length - 1]?.at ?? c.lastAt };
        }));
    };

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [active?.id, active?.messages.length]);

    // Reset panel whenever the active conversation changes
    useEffect(() => {
        setInfoPanelOpen(false);
        setPanelMounted(false);
        setPanelExiting(false);
    }, [active?.id]);

    // Keep mount flag in sync with open flag so exit animation can play
    useEffect(() => {
        if (infoPanelOpen) {
            setPanelExiting(false);
            setPanelMounted(true);
        } else if (panelMounted) {
            setPanelExiting(true);
        }
    }, [infoPanelOpen, panelMounted]);

    const isCommentThread = active?.type === 'comment';
    const canTogglePanel = isCommentThread && !!active?.postPreview;

    return (
        <div style={{ display: 'flex', height: '100%', minHeight: 500, position: 'relative' }}>
            {showConnect && <ConnectModal onConnect={onInstagramConnect} onClose={() => {}} locked busy={instagramBusy} />}
            {/* Conversation list */}
            <div style={{ width: 320, flexShrink: 0, borderRight: '1px solid #e7e5e4', display: 'flex', flexDirection: 'column', background: 'rgba(255,255,255,0.6)' }}>
                {/* Header */}
                <div style={{ padding: '20px 20px 12px', borderBottom: '1px solid #f5f5f4' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
                        <h1 style={{ fontSize: 22, fontWeight: 500, color: '#1f2937', margin: 0, letterSpacing: '-0.02em' }}>Inbox</h1>
                        {totalUnread > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 600, color: '#fff', background: '#111827', borderRadius: 99, padding: '2px 8px' }}>{totalUnread} unread</span>
                        )}
                    </div>
                    {/* Tiny diagnostic — confirms the timezone the bubbles are
                        being formatted in. If this reads "UTC" but the merchant
                        is elsewhere, their browser TZ is misconfigured. */}
                    <div style={{ fontSize: 9, color: '#a8a29e', marginBottom: 10, letterSpacing: '0.05em' }}
                        title="Times below are rendered in this timezone via toLocaleTimeString with the timeZone option">
                        Times in {BROWSER_TZ}
                    </div>
                    {/* Search */}
                    <div style={{ position: 'relative' }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#a8a29e" strokeWidth="2" strokeLinecap="round" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search conversations..."
                            style={{ width: '100%', fontSize: 12, padding: '8px 12px 8px 32px', borderRadius: 99, border: '1px solid #e7e5e4', background: 'rgba(255,255,255,0.8)', outline: 'none', boxSizing: 'border-box' }}
                            onFocus={(e) => e.target.style.borderColor = '#1f2937'} onBlur={(e) => e.target.style.borderColor = '#e7e5e4'}
                        />
                    </div>
                </div>
                {/* Label filters */}
                <div style={{ padding: '8px 12px', display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid #f5f5f4' }}>
                    {LABELS.map((l) => (
                        <button key={l.id} onClick={() => setActiveLabel(l.id)} style={{
                            fontSize: 11, fontWeight: 500, padding: '5px 10px', borderRadius: 99, border: 'none', cursor: 'pointer',
                            transition: 'all 0.15s',
                            ...(activeLabel === l.id
                                ? { background: '#1f2937', color: '#fff' }
                                : { background: 'transparent', color: '#78716c' }),
                        }}>{l.name}</button>
                    ))}
                </div>
                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {filtered.length === 0 && (
                        <div style={{ textAlign: 'center', fontSize: 12, color: '#a8a29e', fontStyle: 'italic', padding: '40px 16px' }}>No conversations match this filter.</div>
                    )}
                    {filtered.map((c) => {
                        const last = c.messages[c.messages.length - 1];
                        const isActive = c.id === active?.id;
                        const isUnread = c.unread > 0;
                        return (
                            <div key={c.id} onClick={() => openConvo(c.id)} style={{
                                display: 'flex', gap: 12, padding: '12px 16px', cursor: 'pointer',
                                borderBottom: '1px solid #f5f5f4', transition: 'background 0.1s',
                                background: isActive ? '#fff' : 'transparent',
                            }}
                                onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.7)'; }}
                                onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                            >
                                {/* Avatar */}
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <div style={{
                                        width: 44, height: 44, borderRadius: 99, padding: 2,
                                        background: isUnread ? 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' : '#e7e5e4',
                                    }}>
                                        <div style={{ width: '100%', height: '100%', borderRadius: 99, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>
                                            {c.avatarEmoji}
                                        </div>
                                    </div>
                                    {isUnread && <div style={{ position: 'absolute', bottom: -1, right: -1, width: 14, height: 14, borderRadius: 99, background: '#111827', border: '2px solid #fff' }} />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                        <div style={{ fontSize: 13, fontWeight: isUnread ? 700 : 500, color: isUnread ? '#1f2937' : '#57534e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{c.handle}</div>
                                        <div style={{ fontSize: 10, flexShrink: 0, color: isUnread ? '#111827' : '#a8a29e', fontWeight: isUnread ? 600 : 400 }}>{formatRel(c.lastAt)}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                                        <span style={{
                                            fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em',
                                            padding: '2px 5px', borderRadius: 4,
                                            ...(c.type === 'dm' ? { background: '#e0f2fe', color: '#0369a1' } : { background: '#fef3c7', color: '#92400e' }),
                                        }}>{c.type === 'dm' ? 'DM' : 'Comment'}</span>
                                        <div style={{ fontSize: 12, color: isUnread ? '#292524' : '#78716c', fontWeight: isUnread ? 500 : 400, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                                            {last?.from === 'me' ? 'You: ' : ''}{last?.text || ''}
                                        </div>
                                        {c.unread > 1 && (
                                            <span style={{ fontSize: 9, fontWeight: 600, color: '#fff', background: '#111827', borderRadius: 99, padding: '1px 6px', flexShrink: 0 }}>{c.unread}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Chat pane + optional right drawer */}
            {active ? (
                <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#fafaf7' }}>
                    {/* Chat header */}
                    <div style={{ padding: '14px 24px', borderBottom: '1px solid #e7e5e4', background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 99, padding: 2, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                            <div style={{ width: '100%', height: '100%', borderRadius: 99, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{active.avatarEmoji}</div>
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1f2937' }}>@{active.handle}</div>
                            <div style={{ fontSize: 11, color: '#78716c' }}>
                                {active.type === 'dm'
                                    ? 'Direct message'
                                    : `Commenting on "${(active.postPreview?.caption ?? '').trim().slice(0, 40) || 'a post'}"`}
                            </div>
                        </div>

                        {/* Thumbnail chip — also toggles the panel. Real Cloudflare
                            media URL when published via this app; gradient + camera
                            emoji as a placeholder for any post we lack a preview for. */}
                        {isCommentThread && active.postPreview && (
                            <button
                                onClick={() => setInfoPanelOpen((v) => !v)}
                                title={infoPanelOpen ? 'Hide post details' : 'Show post details'}
                                style={{
                                    width: 40, height: 40, borderRadius: 10, border: 'none', padding: 0,
                                    overflow: 'hidden', cursor: 'pointer',
                                    background: active.postPreview.mediaUrl ? '#f5f5f4' : 'linear-gradient(135deg, #fde68a, #fed7aa, #fecdd3)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
                                    flexShrink: 0, transition: 'transform 0.15s',
                                    boxShadow: infoPanelOpen ? '0 0 0 2px #fff, 0 0 0 4px #f43f5e' : 'none',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                {active.postPreview.mediaUrl ? (
                                    <img src={active.postPreview.mediaUrl} alt="" draggable={false}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : '📷'}
                            </button>
                        )}

                        {/* Right-panel toggle icon */}
                        {canTogglePanel && (
                            <button
                                onClick={() => setInfoPanelOpen((v) => !v)}
                                title={infoPanelOpen ? 'Close post info' : 'Open post info'}
                                style={{
                                    width: 34, height: 34, borderRadius: 99, border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, transition: 'background 0.15s',
                                    background: infoPanelOpen ? '#1f2937' : 'transparent',
                                    color: infoPanelOpen ? '#fff' : '#78716c',
                                }}
                                onMouseEnter={(e) => { if (!infoPanelOpen) e.currentTarget.style.background = '#f5f5f4'; }}
                                onMouseLeave={(e) => { if (!infoPanelOpen) e.currentTarget.style.background = 'transparent'; }}
                            >
                                {infoPanelOpen ? (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="15" y1="3" x2="15" y2="21" /><path d="M10 15l-3-3 3-3" />
                                    </svg>
                                ) : (
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="15" y1="3" x2="15" y2="21" /><path d="M8 9l3 3-3 3" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>
                    {/* Messages — sorted ascending by timestamp (defensive: backend
                        already returns chronological, but optimistic temp rows
                        and polling races can disturb order). Day-bucket
                        separators clarify cross-day order so a yesterday-evening
                        message above a today-morning message reads correctly. */}
                    <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 620, margin: '0 auto' }}>
                            {(() => {
                                const sorted = [...active.messages].sort((a, b) => (a.at ?? 0) - (b.at ?? 0));
                                let lastDay = null;
                                const rows = [];
                                for (const m of sorted) {
                                    const k = dayKey(m.at);
                                    if (k !== lastDay) {
                                        rows.push(
                                            <div key={`day-${k}`} style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 4px' }}>
                                                <div style={{ flex: 1, height: 1, background: '#e7e5e4' }} />
                                                <div style={{ fontSize: 10, fontWeight: 600, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                    {formatDayLabel(m.at)}
                                                </div>
                                                <div style={{ flex: 1, height: 1, background: '#e7e5e4' }} />
                                            </div>
                                        );
                                        lastDay = k;
                                    }
                                    const mine = m.from === 'me';
                                    rows.push(
                                        <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                                            <div style={{
                                                maxWidth: '70%', borderRadius: 16, padding: '8px 14px', fontSize: 13, lineHeight: 1.5,
                                                ...(mine
                                                    ? { background: '#1f2937', color: '#fff', borderBottomRightRadius: 4 }
                                                    : { background: '#fff', color: '#1f2937', border: '1px solid #e7e5e4', borderBottomLeftRadius: 4 }),
                                                ...(m.failed ? { opacity: 0.6 } : {}),
                                                ...(m.pending ? { opacity: 0.6 } : {}),
                                            }}>
                                                <div>{m.text}</div>
                                                <div style={{ fontSize: 9, marginTop: 4, color: mine ? 'rgba(255,255,255,0.5)' : '#a8a29e' }}
                                                    title={new Date(m.at).toString()}>
                                                    {formatBubbleTimestamp(m.at)}{m.pending ? ' · sending…' : m.failed ? ' · failed' : ''}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                                return rows;
                            })()}
                        </div>
                    </div>
                    {/* Composer — hidden for comment items. Comments are
                        read-only here; replying happens on Instagram (or via
                        the auto-DM that already landed in the user's DM
                        thread under the "DMs" tab). */}
                    {active.type === 'comment' ? (
                        <div style={{ borderTop: '1px solid #e7e5e4', background: '#fafaf7', padding: '14px 20px', textAlign: 'center', fontSize: 12, color: '#78716c', fontStyle: 'italic', flexShrink: 0 }}>
                            Comment view is read-only. Open the user's DM thread to reply.
                        </div>
                    ) : (
                        <div style={{ borderTop: '1px solid #e7e5e4', background: '#fff', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            <input value={text} onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                                placeholder={`Message @${active.handle}...`}
                                style={{ flex: 1, fontSize: 13, padding: '10px 16px', borderRadius: 99, border: '1px solid #e7e5e4', background: '#fafaf9', outline: 'none', boxSizing: 'border-box' }}
                                onFocus={(e) => { e.target.style.borderColor = '#1f2937'; e.target.style.background = '#fff'; }}
                                onBlur={(e) => { e.target.style.borderColor = '#e7e5e4'; e.target.style.background = '#fafaf9'; }}
                            />
                            <button onClick={sendMessage} disabled={!text.trim()} style={{
                                width: 40, height: 40, borderRadius: 99, border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                                color: '#fff', opacity: text.trim() ? 1 : 0.4, transition: 'opacity 0.15s',
                            }}>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                            </button>
                        </div>
                    )}
                </div>

                {/* Right-side post info drawer (comment threads only) */}
                {panelMounted && isCommentThread && active.postPreview && (
                    <PostInfoPanel
                        post={active.postPreview}
                        handle={active.handle}
                        exiting={panelExiting}
                        onExited={() => { setPanelMounted(false); setPanelExiting(false); }}
                        onClose={() => setInfoPanelOpen(false)}
                    />
                )}
                </div>
            ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a8a29e', fontStyle: 'italic', fontSize: 14 }}>
                    Pick a conversation to start chatting.
                </div>
            )}
        </div>
    );
}

// ============================================================================
// PostInfoPanel — right-side drawer shown for comment threads. Mirrors the
// KevoSocial sample: the post itself (IG-style card) and a scrollable
// "Recent comments" feed. Renders the post's real Cloudflare media URL
// when present; falls back to a gradient + emoji placeholder.
// ============================================================================
function PostInfoPanel({ post, handle, onClose, exiting, onExited }) {
    return (
        <>
            <style>{`
@keyframes inboxPanelIn {
  0%   { transform: translateX(100%) scale(0.98); opacity: 0; filter: blur(4px); }
  60%  { transform: translateX(-1%)  scale(1.005); opacity: 1; filter: blur(0); }
  100% { transform: translateX(0)    scale(1);    opacity: 1; filter: blur(0); }
}
@keyframes inboxPanelOut {
  0%   { transform: translateX(0)    scale(1);    opacity: 1; filter: blur(0); }
  100% { transform: translateX(100%) scale(0.98); opacity: 0; filter: blur(4px); }
}
`}</style>
            <aside
                onAnimationEnd={() => { if (exiting) onExited?.(); }}
                style={{
                    width: 340, flexShrink: 0, borderLeft: '1px solid #e7e5e4',
                    background: '#fff', display: 'flex', flexDirection: 'column',
                    boxShadow: exiting ? 'none' : '-20px 0 40px -20px rgba(0,0,0,0.15), -1px 0 0 rgba(0,0,0,0.03)',
                    willChange: 'transform, opacity, filter',
                    animation: exiting
                        ? 'inboxPanelOut 320ms cubic-bezier(.5,0,.75,0) both'
                        : 'inboxPanelIn 460ms cubic-bezier(.2,.85,.2,1.05) both',
                }}
            >
                {/* Header */}
                <div style={{ padding: '14px 20px', borderBottom: '1px solid #e7e5e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                    <div style={{ minWidth: 0, marginRight: 8 }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e', marginBottom: 2 }}>
                            Post details
                        </div>
                        <div style={{ fontSize: 17, fontWeight: 500, color: '#1f2937', letterSpacing: '-0.02em', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {(post.caption ?? '').trim().slice(0, 30) || 'Post'}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        title="Close"
                        style={{
                            width: 30, height: 30, borderRadius: 99, border: 'none', cursor: 'pointer',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: 'transparent', color: '#78716c', transition: 'background 0.15s',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f4'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Scrollable body */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {/* Post — IG-style card */}
                    <div style={{ padding: 16 }}>
                        <div style={{ borderRadius: 12, border: '1px solid #e7e5e4', overflow: 'hidden', background: '#fff' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid #f5f5f4' }}>
                                <div style={{ width: 28, height: 28, borderRadius: 99, padding: 1.5, background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)' }}>
                                    <div style={{ width: '100%', height: '100%', borderRadius: 99, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>
                                        📷
                                    </div>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        studio.latergram
                                    </div>
                                    <div style={{ fontSize: 10, color: '#78716c' }}>
                                        {formatAgo(post.publishedAt)}
                                    </div>
                                </div>
                            </div>
                            {post.mediaUrl ? (
                                <img src={post.mediaUrl} alt="" draggable={false}
                                    style={{ display: 'block', width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', background: '#f5f5f4' }} />
                            ) : (
                                <div style={{ aspectRatio: '1 / 1', background: 'linear-gradient(135deg, #fde68a, #fed7aa, #fecdd3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56 }}>
                                    📷
                                </div>
                            )}
                            {post.caption && (
                                <div style={{ padding: '10px 12px', fontSize: 12, color: '#292524', lineHeight: 1.5 }}>
                                    <span style={{ fontWeight: 600 }}>@{handle ?? 'you'}</span> {post.caption}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent comments */}
                    <div style={{ padding: '16px 16px 24px', borderTop: '1px solid #f5f5f4' }}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.22em', color: '#a8a29e', marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <span>Recent comments</span>
                            <span style={{ textTransform: 'none', letterSpacing: 'normal', color: '#78716c', fontWeight: 500 }}>{post.recentComments?.length ?? 0}</span>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {(post.recentComments ?? []).map((c) => {
                                const username = c.handle || 'unknown';
                                const initial = username.charAt(0).toUpperCase();
                                const fromCurrentUser = c.handle === handle;
                                return (
                                    <div key={c.id} style={{ display: 'flex', gap: 10 }}>
                                        <div style={{ width: 30, height: 30, borderRadius: 99, background: '#1f2937', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                                            {initial}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                                                <span style={{ fontSize: 12, fontWeight: 600, color: fromCurrentUser ? '#111827' : '#1f2937' }}>
                                                    @{username}
                                                </span>
                                                <span style={{ fontSize: 10, color: '#a8a29e' }}>{formatAgo(c.at)}</span>
                                            </div>
                                            <div style={{ fontSize: 12, color: '#44403c', lineHeight: 1.5, marginTop: 2 }}>
                                                {c.text}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
}
